/**
 * Auth-Helper (Welle 6f)
 *
 * Zentrale Stelle für Token-Handling, Logout und JWT-Decoding.
 * Vorher waren `localStorage.getItem('bamoa_token')` und `Bearer dev:${t}` über
 * die ganze App verteilt — jetzt an einem Ort.
 */

const TOKEN_KEY = 'bamoa_token';
const USER_ID_KEY = 'bamoa_user_id';
const USER_NAME_KEY = 'bamoa_user_name';
const TENANT_ID_KEY = 'bamoa_tenant_id';

export function getRawToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** Authorization-Header-Wert für REST-Aufrufe. */
export function getToken(): string {
  const t = getRawToken();
  return t ? `Bearer dev:${t}` : '';
}

export function getDisplayName(): string | null {
  return localStorage.getItem(USER_NAME_KEY);
}

export function getTenantId(): string | null {
  return localStorage.getItem(TENANT_ID_KEY);
}

export function getTenantName(): string | null {
  return localStorage.getItem('bamoa_tenant_name');
}

export function getTenantLogo(): string | null {
  return localStorage.getItem('bamoa_tenant_logo');
}

/**
 * JWT exp lesen (Sekunden seit Epoch). Decodet nur den Payload-Teil (Base64),
 * verifiziert NICHT die Signatur — das ist Sache des Backends. Wir brauchen nur
 * den exp-Claim für den Client-seitigen Auto-Logout-Timer.
 */
export function getTokenExp(): number | null {
  const t = getRawToken();
  if (!t) return null;
  try {
    const parts = t.split('.');
    if (parts.length !== 3) return null;
    // Base64URL → Base64
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Millisekunden bis Token-Ablauf. Negativ wenn schon abgelaufen. null wenn kein Token. */
export function getMsUntilExpiry(): number | null {
  const exp = getTokenExp();
  if (exp === null) return null;
  return exp * 1000 - Date.now();
}

/** True wenn Token vorhanden und nicht abgelaufen. */
export function isTokenValid(): boolean {
  const ms = getMsUntilExpiry();
  return ms !== null && ms > 0;
}

/**
 * Token + verbundene User-Daten aus dem Storage entfernen.
 * Triggert ein 'bamoa:logout'-Event, damit Listener (z.B. App.tsx) reagieren.
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(USER_NAME_KEY);
  localStorage.removeItem(TENANT_ID_KEY);
  window.dispatchEvent(new CustomEvent('bamoa:logout'));
}

/**
 * Wrapper um fetch, der bei 401 automatisch ausloggt.
 * Setzt den Authorization-Header für /api/-Routen automatisch.
 *
 * Welle 8c-pre36: `noLogoutOn401` unterdrückt den Auto-Logout. Für unkritische
 * Hintergrund-Calls (z. B. Telemetrie/Qualitätszusammenfassung am Gesprächsende):
 * ein fehlgeschlagener Telemetrie-POST darf die Berater-Session NIE beenden.
 */
export async function apiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  opts: { noLogoutOn401?: boolean } = {},
): Promise<Response> {
  const isApi = typeof input === 'string' && input.startsWith('/api/');
  const headers = new Headers(init.headers ?? {});
  if (isApi && !headers.has('Authorization')) {
    const auth = getToken();
    if (auth) headers.set('Authorization', auth);
  }
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401 && isApi && !opts.noLogoutOn401) {
    console.warn('[auth] 401 von', input, '— Session abgelaufen, Auto-Logout');
    clearToken();
  }
  return res;
}
